import { Container, Service } from 'typedi';

import { AnnouncementService } from '../services/AnnouncementService';
import { AnnouncementFormStore } from '../stores/AnnouncementFormStore';
import type { IAnnouncementEntity } from '../types/announcement';
import { toRequestLocalizations } from '../utils/announcement';
import { SessionController } from './SessionController';

@Service()
export class AnnouncementFormController {
  private readonly _store = Container.get(AnnouncementFormStore);
  private readonly _service = Container.get(AnnouncementService);
  private readonly _session = Container.get(SessionController);

  /** The route changed: `id` is 'new' or an announcement id; `copyId` is ?copy= of a new one. */
  open = (id: string, copyId: string | null): void => {
    this._store.setRequestedId(id);
    if (id !== 'new') {
      if (this._store.loadedId === id) return;
      // Another announcement: show loading, never the previous one's data.
      this._store.setLoad({ status: 'loading' });
      void this.loadAnnouncement(id);
    } else if (copyId) {
      this._store.setLoad({ status: 'loading' });
      void this.loadCopySource(copyId);
    }
  };

  /** «Повторить»: a copy screen reloads its source; other screens reload by `id`. */
  retry = (id: string, copyId: string | null): void => {
    this._store.setLoad({ status: 'loading' });
    void (copyId ? this.loadCopySource(copyId) : this.loadAnnouncement(id));
  };

  saveDraft = (): Promise<void> => this.run(this.save);

  // Publish and Schedule always store the form first, so unsaved edits are never lost.
  publish = (): Promise<void> => {
    this._store.setConfirming(null);
    return this.run(async () => {
      const saved = await this.save();
      this._store.show(await this._service.publish(saved.id));
    });
  };

  schedule = (): Promise<void> => {
    const { publishAt } = this._store;
    return this.run(async () => {
      const saved = await this.save();
      this._store.show(await this._service.schedule(saved.id, publishAt));
    });
  };

  editPublished = (): Promise<void> =>
    this.run(async () => {
      const current = this._store.announcement;
      if (!current) return;
      const { draft } = this._store;
      const saved = await this._service.editPublished({
        id: current.id,
        important: draft.important,
        pinned: draft.pinned,
        localizations: toRequestLocalizations(draft.localizations),
        audience: this._store.audience,
        endAt: this._store.endAt,
        notifyAgain: this._store.notifyAgain,
      });
      this._store.show(saved);
      this._store.setNotifyAgain(false);
    });

  withdraw = (): Promise<void> => {
    this._store.setConfirming(null);
    const current = this._store.announcement;
    return this.run(async () => {
      if (current) this._store.show(await this._service.withdraw(current.id));
    });
  };

  private loadAnnouncement = async (id: string): Promise<void> => {
    try {
      const a = await this._service.get(id);
      if (this._store.requestedId !== id) return;
      this._store.show(a);
    } catch (e) {
      if (this._store.requestedId !== id) return;
      const error = this._session.reportError(e);
      if (error) this._store.setLoad({ status: 'failed', error });
    }
  };

  private loadCopySource = async (copyId: string): Promise<void> => {
    try {
      const source = await this._service.get(copyId);
      // A version card is one per version: copying it would make a version-less update story.
      if (source.businessVersion !== undefined) {
        this._store.setLoad({ status: 'versionCardCopy' });
        return;
      }
      this._store.startCopy(source);
    } catch (e) {
      const error = this._session.reportError(e);
      if (error) this._store.setLoad({ status: 'failed', error });
    }
  };

  /** Stores the form; a new announcement moves to its own URL. */
  private save = async (): Promise<IAnnouncementEntity> => {
    const current = this._store.announcement;
    const fields = this._store.requestFields;
    const saved = current
      ? await this._service.updateDraft({ id: current.id, ...fields })
      : await this._service.createDraft(fields);
    this._store.show(saved);
    if (!current) this._store.setRedirect({ to: `/announcements/${saved.id}`, replace: true });
    return saved;
  };

  private run = async (action: () => Promise<unknown>): Promise<void> => {
    this._store.startAction();
    try {
      await action();
    } catch (e) {
      const error = this._session.reportError(e);
      if (error) this._store.setServerError(error);
    } finally {
      this._store.finishAction();
    }
  };
}
